import { useState } from 'react';
import { Box, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, Error, Schedule, PropaneSharp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Text from '../../../../../theme/text/Text';
import ShValidatedInput from '../../../../../theme/inputs/ShValidatedInput';
import { InputDropdown } from '../../../../../theme/dropdowns/InputDropdown';
import Shbutton from '../../../../../theme/buttons/Shbutton';

/**
 * EtailizeFieldRenderer Component
 * 
 * Renders different types of authentication fields based on the field definition
 * from the Etailize marketplace connection requirements.
 * 
 * Field type mapping (API fieldType → Component type):
 * - "Text" → Text input field
 * - "Password" → Password field with visibility toggle  
 * - "MarketplaceOption" → Dropdown with predefined options
 * - "OAuthButton" → OAuth authentication button
 * 
 * Props:
 * - field: Field definition object (processed from API response)
 * - value: Current field value
 * - onChange: Function to handle field value changes
 * - error: Error message for this field
 * - onClearError: Function to clear error for this field
 * - validationStatus: Field validation status ('valid', 'invalid', 'pending')
 */
const EtailizeFieldRenderer = ({ field, value, onChange, error, onClearError, validationStatus, disabled=false }) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);

    // Sanitize error prop to prevent null/undefined issues
    const sanitizedError = error === null ? undefined : error;

    // Early return if field is invalid
    if (!field || !field.name || typeof field.name !== 'string' || field.name.trim() === '') {
        console.warn('EtailizeFieldRenderer: Invalid field provided:', field);
        return (
            <Box sx={{ mb: 2 }}>
                <Text color="error" variant="body2">
                    {t('invalid_field_configuration')}
                </Text>
            </Box>
        );
    }

    // Handle field value change
    const handleChange = (key, newValue) => {
        // Debug logging to understand the issue
        console.log('EtailizeFieldRenderer handleChange called:', {
            fieldName: field.name,
            key: key,
            newValue: newValue,
            field: field
        });
        
        // Ensure we have a valid field name before calling onChange
        if (field && field.name) {
            onChange(field.name, newValue);
            if (sanitizedError && onClearError) {
                onClearError(field.name);
            }
        } else {
            console.warn('EtailizeFieldRenderer: Invalid field or field name:', field);
        }
    };

    // Toggle password visibility
    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    // Handle OAuth button click with popup monitoring
    const handleOAuthClick = async () => {
        const oauthUrl = field.oauth_url || field.valueOptions?.OauthUrl;
        if (!oauthUrl) return;

        const openPopup = (uri) => {
            const monitorPopup = windowHandle => new Promise((resolve, reject) => {
                const intervalId = setInterval(() => {
                    if (!windowHandle || windowHandle.closed) {
                        reject("Window was closed");
                        clearInterval(intervalId);
                    }
                }, 500);

                window.addEventListener('message', function messageListener(e) {
                    //Check e.origin === location.origin (optional security check)
                    if (e.isTrusted === true) {
                        const result = e.data;
                        if (result) {
                            resolve(result);
                            window.removeEventListener('message', messageListener);
                            clearInterval(intervalId);
                        }
                    }
                    windowHandle.close();
                });
            });

            const handle = window.open(uri, "_blank", "width=600,height=700");
            return monitorPopup(handle);
        };

        try {
            const result = await openPopup(oauthUrl);
            // Handle the OAuth result
            if(result && result.Properties && result.Properties.SELLING_PARTNER_ID){
                if (result && result.Code) {
                    handleChange(field.name, result.Code + "||||" + result.Properties.SELLING_PARTNER_ID);
                }
            } else if(result && result.properties && result.properties.SELLING_PARTNER_ID){
                if (result && result.code) {
                    handleChange(field.name, result.code + "||||" + result.properties.SELLING_PARTNER_ID);
                }
            } else if (result && result.code) {
                handleChange(field.name, result.code);
            } else if (result && result.Code) {
                handleChange(field.name, result.Code);
            } else if (result && result.Code) {
                handleChange(field.name, result.Code);
            }
        
        
            // Trigger callback if provided
            if (field.onOAuthComplete) {
                field.onOAuthComplete(result);
            }
        } catch (error) {
            console.warn('OAuth flow cancelled or failed:', error);
            // Optionally handle error state
            if (field.onOAuthError) {
                field.onOAuthError(error);
            }
        }
    };

    // Render validation status icon
    const renderValidationIcon = () => {
        if (!validationStatus) return null;
        
        const iconStyle = {
            fontSize: '20px',
            marginLeft: '8px'
        };
        
        switch (validationStatus) {
            case 'valid':
                return <CheckCircle sx={{ ...iconStyle, color: '#4caf50' }} />;
            case 'invalid':
                return <Error sx={{ ...iconStyle, color: '#f44336' }} />;
            case 'pending':
                return <Schedule sx={{ ...iconStyle, color: '#ff9800' }} />;
            default:
                return null;
        }
    };

    // Render field label with required indicator and validation icon
    const renderFieldLabel = () => (
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Text fontWeight="medium">
                {field.label || field.name}
                {field.required && (
                    <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                )}
            </Text>
            {renderValidationIcon()}
            {field.description && (
                <Text variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                    ({field.description})
                </Text>
            )}
        </Box>
    );

    // Render different field types
    switch (field.type) {
        case 'Text':
            return (
                <Box sx={{ mb: 2 }}>
                    {renderFieldLabel()}
                    <ShValidatedInput
                        name={field.name}
                        value={value === undefined || value === null ? '' : String(value)}
                        onChange={handleChange}
                        error={sanitizedError}
                        placeholder={field.placeholder || field.label}
                        fullWidth
                        disabled={disabled}
                    />
                    {field.helpText && (
                        <Text variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                            {field.helpText}
                        </Text>
                    )}
                </Box>
            );

        case 'Password':
            return (
                <Box sx={{ mb: 2 }}>
                    {renderFieldLabel()}
                    <ShValidatedInput
                        name={field.name}
                        type={showPassword ? 'text' : 'password'}
                        value={value === undefined || value === null ? '' : String(value)}
                        onChange={handleChange}
                        error={sanitizedError}
                        placeholder={field.placeholder || field.label}
                        fullWidth
                        disabled={disabled}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleTogglePasswordVisibility}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {field.helpText && (
                        <Text variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                            {field.helpText}
                        </Text>
                    )}
                </Box>
            );

        case 'MarketplaceOption':
            // Convert valueOptions object to array format expected by InputDropdown
            // Format: {"The Netherlands": "NL", "Belgium (Dutch)": "BE"} 
            // → [{label: "The Netherlands", value: "NL"}, {label: "Belgium (Dutch)", value: "BE"}]
            const options = field.valueOptions && typeof field.valueOptions === 'object'
                ? Object.entries(field.valueOptions).map(([label, value]) => ({
                    label,
                    value
                }))
                : [];
            
            return (
                <Box sx={{ mb: 2 }}>
                    {renderFieldLabel()}
                    <InputDropdown
                        name={field.name}
                        noSelection={t('choose_option')}
                        displayName="label"
                        options={options}
                        selected={
                            options.findIndex(option => option.value === (value === undefined || value === null ? '' : value))
                        }
                        onChange={(selectedIndex) => {
                            const selectedOption = options[selectedIndex];
                            handleChange(field.name, selectedOption?.value);
                        }}
                        searchOptions={{
                            enabled: options.length > 5,
                            placeholder: t('search')
                        }}
                        closeOnSelection
                        error={sanitizedError}
                        disabled={disabled}
                    />
                    {field.helpText && (
                        <Text variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                            {field.helpText}
                        </Text>
                    )}
                </Box>
            );

        case 'OAuthButton':
            return (
                <Box sx={{ mb: 2 }}>
                    {renderFieldLabel()}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Shbutton
                            variant="outlined"
                            onClick={handleOAuthClick}
                            disabled={!field.oauth_url && !field.valueOptions?.OauthUrl}
                            fullWidth
                        >
                            {field.button_text || t('authenticate_with_oauth')}
                        </Shbutton>
                        
                        {value && (
                            <Box sx={{ 
                                p: 1, 
                                backgroundColor: '#e8f5e8',
                                borderRadius: 1,
                                border: '1px solid #4caf50'
                            }}>
                                <Text variant="body2" color="success.main">
                                    ✓ {t('authentication_successful')}
                                </Text>
                            </Box>
                        )}
                        
                        {sanitizedError && (
                            <Box sx={{ 
                                p: 1, 
                                backgroundColor: '#ffebee',
                                borderRadius: 1,
                                border: '1px solid #f44336'
                            }}>
                                <Text variant="body2" color="error">
                                    {sanitizedError}
                                </Text>
                            </Box>
                        )}
                    </Box>
                    
                    {field.helpText && (
                        <Text variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                            {field.helpText}
                        </Text>
                    )}
                </Box>
            );

        default:
            // Enhanced fallback for unknown field types
            const isDevelopment = process.env.NODE_ENV === 'development';
            
            // Log warning in development mode
            if (isDevelopment) {
                console.warn('Unsupported field type encountered:', {
                    fieldName: field.name,
                    fieldType: field.type,
                    originalFieldType: field.fieldType,
                    field: field
                });
            }
            
            return (
                <Box sx={{ mb: 2 }}>
                    {renderFieldLabel()}
                    <Box sx={{ 
                        p: 2, 
                        border: '1px dashed #ccc', 
                        borderRadius: 1,
                        textAlign: 'center',
                        backgroundColor: '#fafafa'
                    }}>
                        <Text color="textSecondary">
                            {t('unsupported_field_type', { type: field.type })}
                        </Text>
                        <Text variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                            Field: {field.name} | Type: {field.type}
                            {field.fieldType && field.fieldType !== field.type && (
                                <span> | Original: {field.fieldType}</span>
                            )}
                        </Text>
                        {isDevelopment && (
                            <Text variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                Development: Check console for field details
                            </Text>
                        )}
                    </Box>
                </Box>
            );
    }
};

export default EtailizeFieldRenderer;